import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MasterPurgeResult } from '@loot/shared';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.ts';
import { useMe } from '../auth.tsx';
import { FormModal, FormSection } from './FormModal.tsx';
import { Button, ErrorBox, Field, Input, Loading, Textarea } from './ui.tsx';

/**
 * Botão "Excluir" do administrador master (ADR 0051). Só aparece para a conta master com a
 * exclusão ligada; para as demais pessoas não existe. Abre a prévia do que sai junto, pede o motivo
 * e a palavra de confirmação, e apaga tudo de uma vez.
 *
 * `table` é a tabela do registro no banco ("schema.tabela"), `id` o identificador. `onDeleted` leva
 * a pessoa para fora da tela do registro apagado (ex.: de volta à lista). `label`, `subject` e
 * `scope` dizem o objeto e o alcance quando a tela mostra mais de um (ex.: o orçamento inteiro ao
 * lado dos lançamentos dele).
 */
export function MasterDeleteButton({
  table,
  id,
  onDeleted,
  compact = false,
  label,
  subject,
  scope,
}: {
  table: string;
  id: string;
  onDeleted?: () => void;
  /** Só o ícone (linhas de tabela). */
  compact?: boolean;
  /** Texto do botão e título da janela no lugar de "Excluir" / "Excluir registro". */
  label?: string;
  /** O que será apagado, como a pessoa conhece (nome e código). */
  subject?: string;
  /** Aviso do alcance, em destaque antes da prévia. */
  scope?: string;
}) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const [open, setOpen] = useState(false);
  if (!me?.master?.purgeEnabled) return null;
  const text = label ?? t('masterPurge.button');
  return (
    <>
      {compact ? (
        // Na linha da lista, discreto: o dado aparece primeiro, e o vermelho só no mouse ou no foco.
        <button
          type="button"
          onClick={() => setOpen(true)}
          title={text}
          aria-label={text}
          className="inline-flex size-8 items-center justify-center rounded-md text-muted hover:bg-danger/10 hover:text-danger focus-visible:text-danger"
        >
          <Trash2 size={16} aria-hidden />
        </button>
      ) : (
        <Button variant="danger" onClick={() => setOpen(true)} title={text} aria-label={text}>
          <Trash2 size={16} aria-hidden />
          {text}
        </Button>
      )}
      {open && (
        <MasterDeleteDialog
          table={table}
          id={id}
          label={label}
          subject={subject}
          scope={scope}
          mfa={me.mfa?.verified !== false}
          onClose={() => setOpen(false)}
          onDeleted={() => {
            setOpen(false);
            onDeleted?.();
          }}
        />
      )}
    </>
  );
}

function MasterDeleteDialog({
  table,
  id,
  label,
  subject,
  scope,
  mfa,
  onClose,
  onDeleted,
}: {
  table: string;
  id: string;
  label?: string;
  subject?: string;
  scope?: string;
  mfa: boolean;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [word, setWord] = useState('');
  const confirmWord = t('masterPurge.confirmWord');
  const preview = useQuery({
    queryKey: ['master-purge', table, id],
    queryFn: () => api.post<MasterPurgeResult>('/master/purge/preview', { table, id }),
    enabled: mfa,
    retry: false,
    gcTime: 0,
  });
  const purge = useMutation({
    mutationFn: () => api.post<MasterPurgeResult>('/master/purge', { table, id, reason }),
    onSuccess: () => {
      // Sai da ficha apagada antes; depois, todas as listas e fichas são lidas de novo.
      onDeleted();
      void queryClient.invalidateQueries();
    },
  });
  const ready =
    mfa &&
    !!preview.data &&
    reason.trim().length >= 5 &&
    word.trim().toUpperCase() === confirmWord.toUpperCase();
  return (
    <FormModal
      open
      size="md"
      title={label ?? t('masterPurge.title')}
      description={t('masterPurge.description')}
      dirty={reason.trim() !== '' || word.trim() !== ''}
      saving={purge.isPending}
      error={purge.error}
      saveLabel={t('masterPurge.confirm')}
      saveTone="danger"
      onSave={() => ready && purge.mutate()}
      onClose={onClose}
    >
      {!mfa ? (
        <p className="text-sm">{t('masterPurge.mfaRequired')}</p>
      ) : preview.isPending ? (
        <Loading />
      ) : preview.data ? (
        <FormSection columns={1}>
          {(subject || scope) && (
            <div role="alert" className="rounded-md border border-danger bg-danger/5 p-3 text-sm">
              {subject && <p className="font-semibold">{subject}</p>}
              {scope && <p className={subject ? 'mt-1' : undefined}>{scope}</p>}
            </div>
          )}
          <div className="text-sm">
            <p className="font-medium">{t('masterPurge.summary', { count: preview.data.total })}</p>
            <ul className="mt-2 max-h-48 list-disc overflow-y-auto pl-5">
              {preview.data.tables.map((x) => (
                <li key={x.table}>
                  <code className="font-mono text-xs">{x.table}</code>: {x.records}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-muted">{t('masterPurge.history')}</p>
          </div>
          <Field label={t('masterPurge.reason')} required>
            <Textarea required value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <Field label={t('masterPurge.typeWord', { word: confirmWord })} required>
            <Input required value={word} onChange={(e) => setWord(e.target.value)} />
          </Field>
        </FormSection>
      ) : (
        <ErrorBox error={preview.error} />
      )}
    </FormModal>
  );
}
